/**
 * Session Recovery module
 *
 * Handles the "never lose progress" guarantee:
 * - On app init, reads IndexedDB for any files from a previous interrupted session
 * - Resumes processing for any files that were 'pending' or 'processing' when the
 *   browser was closed/reloaded
 * - Files already 'done' are surfaced immediately to the UI without reprocessing
 * - Hydrates or initializes the active SessionState and keeps it synchronized
 *
 * Safari OPFS eviction note: we call requestPersistentStorage() here to request
 * that WebKit does not evict our data under storage pressure.
 */

import {
  getAllFileRecords,
  upsertFileRecord,
  clearAllFileRecords,
  loadSession,
  saveSession,
  clearSession,
} from './storage/idb'
import {
  requestPersistentStorage,
  getStorageEstimate,
  deleteFromOPFS,
  deleteDirectoryFromOPFS,
} from './storage/opfs'
import { nanoid } from './nanoid'
import { mapConcurrent } from './concurrency'
import type { FileRecord, SessionState } from './storage/types'
import {
  processFile,
  PROCESS_CONCURRENCY,
  type ProcessingOptions,
  type ProgressCallback,
} from './pipeline'

const STORAGE_WARN_THRESHOLD = 0.85 // warn at 85% quota used

export interface SessionRecoveryResult {
  session: SessionState
  completedFiles: FileRecord[]
  needsReprocessing: FileRecord[]
  failedFiles: FileRecord[]
  allFiles: FileRecord[]
  storageWarning: boolean
  /** Backward-compatibility alias for failedFiles */
  resumedFiles: FileRecord[]
}

/**
 * Called once on app startup.
 * Returns files from previous session grouped by their recovery action needed,
 * and initializes or restores the active session metadata in IndexedDB.
 */
export async function recoverSession(): Promise<SessionRecoveryResult> {
  await requestPersistentStorage()

  const { quota, usage } = await getStorageEstimate()
  const storageWarning = quota > 0 && usage / quota > STORAGE_WARN_THRESHOLD

  const allRecords = await getAllFileRecords()

  const completedFiles = allRecords.filter((r) => r.status === 'done')
  const needsReprocessing = allRecords.filter(
    (r) => r.status === 'pending' || r.status === 'processing',
  )
  const failedFiles = allRecords.filter((r) => r.status === 'error')

  // Reset any 'processing' files to 'pending' — they were interrupted mid-flight
  // and their partial results may be incomplete in OPFS
  await Promise.all(
    needsReprocessing
      .filter((r) => r.status === 'processing')
      .map((r) => upsertFileRecord({ ...r, status: 'pending', updatedAt: Date.now() })),
  )

  // Retrieve existing session or initialize a fresh active session
  let session = await loadSession()
  if (!session) {
    session = {
      id: nanoid(),
      startedAt: Date.now(),
      lastActivityAt: Date.now(),
      totalFiles: allRecords.length,
    }
  } else {
    session = {
      ...session,
      lastActivityAt: Date.now(),
      totalFiles: allRecords.length,
    }
  }
  await saveSession(session)

  return {
    session,
    completedFiles,
    needsReprocessing,
    failedFiles,
    allFiles: allRecords,
    storageWarning,
    resumedFiles: failedFiles,
  }
}

/**
 * Continues processing files that were interrupted in a previous session.
 * Uses the same pipeline and options as a normal batch job with bounded concurrency.
 */
export async function reprocessInterrupted(
  files: FileRecord[],
  options: ProcessingOptions,
  onProgress: ProgressCallback,
): Promise<void> {
  if (files.length === 0) return

  await mapConcurrent(files, PROCESS_CONCURRENCY, (record) =>
    processFile(record, options, onProgress),
  )
}

/**
 * Clears all session data — files from OPFS + records from IndexedDB + session record.
 * Called when the user explicitly clicks "New session" or "Clear all".
 */
export async function clearAllSessionData(fileIds: string[]): Promise<void> {
  await Promise.all([
    clearAllFileRecords(),
    clearSession(),
    ...fileIds.flatMap((id) => [
      deleteFromOPFS(`originals/${id}`),
      deleteDirectoryFromOPFS(`results/${id}`),
    ]),
  ])
}
